import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { doc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { linkPhoneToUid } from '../../services/userProfileService';
import { IoSearchOutline, IoCloseCircle, IoCheckmarkCircle } from 'react-icons/io5';

const USER_TYPES = [
  { key: 'teacher', label: '강사' },
  { key: 'student', label: '학생' },
  { key: 'assistant', label: '조교' },
  { key: 'parent', label: '학부모' },
];

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUser, logout } = useAuth();

  const [selectedType, setSelectedType] = useState(userData?.role || 'student');
  const [form, setForm] = useState({
    name: userData?.name || currentUser?.displayName || '',
    school: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 학부모 - 학생 검색
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // 학생 - 클래스 선택
  const [classList, setClassList] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classLoading, setClassLoading] = useState(false);
  const [classSheetOpen, setClassSheetOpen] = useState(false);

  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClasses = async () => {
    setClassLoading(true);
    try {
      const snap = await getDocs(collection(db, 'classes'));
      setClassList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('클래스 목록 로드 실패:', err);
    } finally {
      setClassLoading(false);
    }
  };

  const toggleClass = (cls) => {
    setSelectedClasses((prev) =>
      prev.find((c) => c.id === cls.id)
        ? prev.filter((c) => c.id !== cls.id)
        : [...prev, cls]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleStudentSearch = async () => {
    if (!studentSearch.trim()) return;
    setSearching(true);
    setShowResults(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const keyword = studentSearch.trim().toLowerCase();
      const students = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((s) => s.role === 'student')
        .filter((s) =>
          (s.name || '').toLowerCase().includes(keyword) ||
          (s.email || '').toLowerCase().includes(keyword)
        );
      setSearchResults(students);
    } catch (err) {
      console.error('학생 검색 실패:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setShowResults(false);
    setStudentSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (selectedType === 'parent' && !selectedStudent) {
      setError('수강 학생을 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      const uid = currentUser.uid;
      const updates = {
        name: form.name.trim(),
        role: selectedType,
        status: 'pending',
        updatedAt: serverTimestamp(),
      };

      // 이미 phoneE164가 있으면 유지, 없으면 빈값
      if (!userData?.phoneE164) {
        updates.phoneE164 = '';
        updates.phoneVerified = false;
      }

      if (selectedType === 'parent') {
        updates.children = [selectedStudent.uid];
        updates.childrenNames = [selectedStudent.name];
      } else if (selectedType === 'student') {
        updates.school = form.school;
        updates.classes = selectedClasses.map((c) => c.id);
        updates.classNames = selectedClasses.map((c) => c.name);
      }

      await setDoc(doc(db, 'users', uid), updates, { merge: true });

      // phone이 있으면 인덱스도 업데이트
      if (userData?.phoneE164) {
        await linkPhoneToUid({ uid, phoneE164: userData.phoneE164 });
      }

      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('프로필 저장 실패:', err);
      setError('프로필 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>프로필 완성</Title>
        <Desc>기본 정보를 입력해주세요.</Desc>


        <FormArea onSubmit={handleSubmit}>
          <Label>사용자 유형</Label>
          <TypeGrid>
            {USER_TYPES.map((type) => (
              <TypeButton
                key={type.key}
                type="button"
                $active={selectedType === type.key}
                onClick={() => {
                  setSelectedType(type.key);
                  setSelectedStudent(null);
                  setSearchResults([]);
                  setShowResults(false);
                  setSelectedClasses([]);
                }}
              >
                {type.label}
              </TypeButton>
            ))}
          </TypeGrid>

          <Label>이름</Label>
          <Input
            name="name"
            placeholder=""
            value={form.name}
            onChange={handleChange}
          />

          {selectedType === 'student' && (
            <>
              <Label>소속 학교/학원</Label>
              <Input
                name="school"
                placeholder=""
                value={form.school}
                onChange={handleChange}
              />

              <Label>클래스 선택</Label>
              <ClassSelectBtn type="button" onClick={() => setClassSheetOpen(true)}>
                {selectedClasses.length > 0
                  ? `${selectedClasses.map(c => c.name).join(', ')} (${selectedClasses.length}개)`
                  : '클래스를 선택하세요'}
              </ClassSelectBtn>

              {classSheetOpen && (
                <SheetOverlay onClick={() => setClassSheetOpen(false)}>
                  <SheetContent onClick={(e) => e.stopPropagation()}>
                    <SheetHandle />
                    <SheetTitle>클래스 선택</SheetTitle>
                    {classLoading ? (
                      <HintText>불러오는 중...</HintText>
                    ) : classList.length === 0 ? (
                      <HintText>등록된 클래스가 없습니다</HintText>
                    ) : (
                      <SheetList>
                        {classList.map((cls) => {
                          const isSelected = selectedClasses.find((c) => c.id === cls.id);
                          return (
                            <SheetItem
                              key={cls.id}
                              type="button"
                              $selected={!!isSelected}
                              onClick={() => toggleClass(cls)}
                            >
                              <SheetItemLeft>
                                <ClassName>{cls.name}</ClassName>
                                {cls.teacherName && <ClassInfo>{cls.teacherName}</ClassInfo>}
                              </SheetItemLeft>
                              {isSelected && <IoCheckmarkCircle size={22} color="#4A7CFE" />}
                            </SheetItem>
                          );
                        })}
                      </SheetList>
                    )}
                    <SheetDoneBtn type="button" onClick={() => setClassSheetOpen(false)}>
                      선택 완료
                    </SheetDoneBtn>
                  </SheetContent>
                </SheetOverlay>
              )}
            </>
          )}

          {selectedType === 'parent' && (
            <>
              <Label>수강 학생 선택</Label>
              {selectedStudent ? (
                <SelectedChip>
                  <span>{selectedStudent.name}</span>
                  <RemoveBtn type="button" onClick={() => setSelectedStudent(null)}>
                    <IoCloseCircle size={20} />
                  </RemoveBtn>
                </SelectedChip>
              ) : (
                <div>
                  <SearchRow>
                    <SearchInput
                      placeholder="학생 이름으로 검색"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleStudentSearch(); } }}
                    />
                    <SearchBtn type="button" onClick={handleStudentSearch} disabled={searching}>
                      <IoSearchOutline size={18} />
                    </SearchBtn>
                  </SearchRow>
                  {showResults && (
                    <ResultsList>
                      {searching ? (
                        <HintText>검색 중...</HintText>
                      ) : searchResults.length === 0 ? (
                        <HintText>검색 결과가 없습니다</HintText>
                      ) : (
                        searchResults.map((s) => (
                          <ResultItem key={s.uid} onClick={() => handleSelectStudent(s)}>
                            <span>{s.name}</span>
                            {s.school && <ResultSub>{s.school}</ResultSub>}
                          </ResultItem>
                        ))
                      )}
                    </ResultsList>
                  )}
                </div>
              )}
            </>
          )}

          {error && <ErrorText>{error}</ErrorText>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '저장 중...' : '프로필 저장'}
          </SubmitBtn>
        </FormArea>

        <LogoutBtn onClick={logout}>
          로그아웃
        </LogoutBtn>
      </Card>
    </Container>
  );
};

export default CompleteProfilePage;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: linear-gradient(180deg, #EEF2FF 0%, #F5F7FA 40%);
  padding: 24px;
  padding-top: 48px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const Title = styled.p`
  font-size: 20px;
  text-align: center;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
  margin-bottom: 20px;
`;

const EmailBox = styled.div`
  text-align: center;
  padding: 10px;
  background: #F0F4FF;
  border-radius: 8px;
  font-size: 14px;
  color: #4A7CFE;
  margin-bottom: 16px;
`;

const FormArea = styled.form``;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #333;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const TypeButton = styled.button`
  padding: 12px;
  border-radius: 8px;
  border: 2px solid ${({ $active }) => $active ? '#4A7CFE' : '#E5E7EB'};
  background: ${({ $active }) => $active ? '#EEF2FF' : 'white'};
  font-size: 14px;
  color: ${({ $active }) => $active ? '#4A7CFE' : '#666'};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: #4A7CFE; outline: none; }
`;

const ClassSelectBtn = styled.button`
  width: 100%;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: white;
  text-align: left;
  font-size: 14px;
  color: #888;
`;

const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const SheetContent = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 70vh;
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 12px 20px 24px;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto 12px;
`;

const SheetTitle = styled.p`
  font-size: 16px;
  color: #1a1a1a;
  margin-bottom: 12px;
`;

const SheetList = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SheetItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  background: ${({ $selected }) => $selected ? '#F0F4FF' : 'transparent'};
  &:last-child { border-bottom: none; }
`;

const SheetItemLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const SheetDoneBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: 28px;
  background: linear-gradient(135deg, #4A7CFE, #6C63FF);
  color: white;
  font-size: 15px;
`;

const ClassName = styled.span`
  font-size: 14px;
`;

const ClassInfo = styled.span`
  display: block;
  font-size: 12px;
  color: #888;
  margin-top: 2px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: #4A7CFE; outline: none; }
`;

const SearchBtn = styled.button`
  padding: 0 12px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: #888;
  border: 1px solid #ddd;
  &:disabled { opacity: 0.5; }
`;

const ResultsList = styled.div`
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const ResultItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
  &:hover { background: #F0F4FF; }
`;

const ResultSub = styled.span`
  font-size: 12px;
  color: #888;
`;

const SelectedChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 2px solid #4A7CFE;
  border-radius: 8px;
  background: #EEF2FF;
  font-size: 14px;
`;

const RemoveBtn = styled.button`
  color: #999;
  display: flex;
  &:hover { color: #EF4444; }
`;

const HintText = styled.p`
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #999;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 13px;
  margin-top: 8px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #4A7CFE, #6C63FF);
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;

const LogoutBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;
